import { useState } from 'react';
import { useLoaderData } from 'react-router';

import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import type { loader } from '~/routes/_app.product.$urlSegment';
import { detailCta, DetailTileCard } from '~/ui-rocco/pages/detail-shared';
import { useInfiniteScrollSentinel } from '~/ui-rocco/pages/category-page/hooks/useInfiniteScrollSentinel';
import { PRODUCT_TILE_GRID, PRODUCT_TILE_MEDIA } from './product-tile-grid';
import type { SimilarProducts } from './use-similar-products';

/**
 * Inner content of the Similar Products block (Figma 1841:9733/10119/10162) —
 * the two-tone "More in {category} category" heading, the category breadcrumb,
 * the 2-col (mobile) / 4-col (desktop) tile grid and the "Load More" pill. No
 * eyebrow / hairline / leading spacer: the standalone section and the accordion
 * row each supply their own surrounding chrome.
 */
export const SimilarProductsGrid = ({
    tiles,
    hasMore,
    isLoading,
    loadMore,
    hideLoadMore = false,
    tileTextClassName,
}: SimilarProducts & {
    /** Suppress the inline pill when the Magic Island owns "Load More". */
    hideLoadMore?: boolean;
    /** Extra classes on each tile's text (the accordion row runs it smaller). */
    tileTextClassName?: string;
}) => {
    const { primaryCategory } = useLoaderData<typeof loader>();

    // Same hybrid as the category grid: Load More is a real, crawlable control
    // for the first step, and arms infinite scroll for everything after it, so
    // the reader opts in once rather than the page growing unasked.
    const [infiniteEnabled, setInfiniteEnabled] = useState(false);
    // The shared sentinel keeps one observer for the element's lifetime. The
    // observer must not be rebuilt per load: a fresh one reports an
    // already-intersecting target immediately, so tearing it down and
    // recreating it each cycle fetched again with no reader input at all.
    //
    // The margin has to be something a single page clears, or the sentinel
    // never leaves the zone and paging runs to the end of the category on its
    // own. A page here is 4 tiles over two mobile columns — two rows — where
    // the grid's own 2000px default would never be cleared.
    const setSentinelRef = useInfiniteScrollSentinel({
        enabled: infiniteEnabled && hasMore && !isLoading,
        onTrigger: () => loadMore?.(),
        rootMarginPx: 600,
    });

    return (
        <>
            {/* Product title's scale, not the 200px brand statement — this is a
                heading inside the page, not a poster. */}
            <h2 className="m-0 font-sans font-normal text-[#1f1f21] max-md:text-[2rem] max-md:leading-[1.08] max-md:tracking-[-0.02em] md:text-[3.9rem] md:leading-[1.08] md:tracking-[-0.125rem]">
                More in{' '}
                {/* The category is set apart in grey because it goes somewhere:
                    it was reading as decoration, so it is a link. */}
                {primaryCategory?.Link ? (
                    <RoccoLink
                        to={primaryCategory.Link}
                        className="text-[#767676] transition-colors hover:text-[#1f1f21] md:text-[#8f8f8f] md:hover:text-[#1f1f21]"
                    >
                        {primaryCategory.PrettyTitle}
                    </RoccoLink>
                ) : (
                    <span className="text-[#767676] md:text-[#8f8f8f]">
                        {primaryCategory?.PrettyTitle}
                    </span>
                )}{' '}
                category
            </h2>

            <div className={cn(PRODUCT_TILE_GRID, 'max-md:mt-6 md:mt-[3rem]')}>
                {tiles.map((tile) => (
                    <DetailTileCard
                        key={tile.id}
                        tile={tile}
                        mediaClassName={PRODUCT_TILE_MEDIA}
                        textClassName={tileTextClassName}
                    />
                ))}
            </div>

            {hasMore && !hideLoadMore && !infiniteEnabled ? (
                <div className="mt-[3rem] flex justify-center max-md:mt-10">
                    <button
                        type="button"
                        onClick={() => {
                            setInfiniteEnabled(true);
                            loadMore?.();
                        }}
                        disabled={isLoading}
                        className={detailCta(
                            'filled-light',
                            'disabled:opacity-50 max-md:h-[3.125rem] max-md:w-full md:min-w-[14.4rem]',
                        )}
                    >
                        {isLoading ? 'Loading…' : 'Load More'}
                    </button>
                </div>
            ) : null}
            {hasMore && infiniteEnabled ? (
                <div ref={setSentinelRef} aria-hidden="true" className="h-px" />
            ) : null}
        </>
    );
};
